//
// Copyright (c) 2026 Comcast
//

import { Link } from 'react-router'
import styled, { css } from 'styled-components'
import { useSelector } from 'react-redux'
import { useContext } from 'react'
import { AbilityContext } from 'ability-context'

import { useCheckFeature } from 'utils/hooks'

import NavPartners from './NavPartners'
import NavTickets from './NavTickets'
import NavReports from './NavReports'
import NavConfiguration from './NavConfiguration'
import NavAppRequests from './NavAppRequests'
import NavAbout from './NavAbout'
import GlobalSearch from './GlobalSearch'
import GlobalSearchNew from './GlobalSearch/Index'
import NavFeedback from './NavFeedback'
import NavUser from './NavUser'
import NavSatClients from './NavSatClients'

import EnvironmentStripe, { getEnvironmentStripeInfo } from './EnvironmentStripe'

import * as sessionUserSelector from 'selectors/sessionUser'
import colors from 'components/utils/colors'

import { NAV_BAR_HEIGHT } from 'constants/constants'

import * as ticketingSystemsSelector from 'selectors/ticketingSystems'

const StyledLogo = styled(Link)`
  margin-right: 12px;
`

const StyledPartnerLogo = styled.img`
  width: auto;
  height: 18px;
`

const StyledNavBar = styled.header`
  height: ${NAV_BAR_HEIGHT}px;
  z-index: 1500;
  margin-bottom: ${props => props.marginBottom};
`

const NavBarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  top: 0;
  left: 0;
  right: 0;
  min-height: ${NAV_BAR_HEIGHT}px;
  border-bottom: 1px solid ${colors.secondary70};
  background-color: #fff;
`

const styleAsLeft = css`
  padding-left: 16px;
`

const styleAsRight = css`
  padding-right: 0;
  justify-content: flex-end;
`

const styleAsCentral = css`
  justify-content: center;
`

const NavBarSection = styled.div`
  display: flex;
  align-items: center;
  flex-basis: 33%;

  ${props => props.left && styleAsLeft}
  ${props => props.right && styleAsRight}
  ${props => props.central && styleAsCentral}
`

const NavBar = () => {
  const userPartnerIcon = useSelector(sessionUserSelector.partnerIcon)
  const canAccessApiDoc = useSelector(sessionUserSelector.canAccessApiDoc)
  const ticketingSystemsNotEmpty = useSelector(ticketingSystemsSelector.ticketingSystemsNotEmpty)
  const userIsPartnerManager = useSelector(sessionUserSelector.isPartnerManager)
  const isAnyAdmin = useSelector(sessionUserSelector.isAnyAdmin)

  const ability = useContext(AbilityContext)
  const canImpersonate = ability.can('switch', 'Impersonation')
  const canReadAdminPages = ability.can('read', 'AdminPages')
  const canReadPowerUserPages = ability.can('read', 'PowerUserPages')

  const IS_RELAY_3268_QUICK_SEARCH = useCheckFeature('RELAY_3268_QUICK_SEARCH')
  const IS_PARTNERS_HAVE_ACCESS_TO_SAT_CLIENTS = useCheckFeature('RELAY_3359_PARTNERS_HAVE_ACCESS_TO_SAT_CLIENTS')

  const PartnerLogo = () => {
    return userPartnerIcon ? <StyledPartnerLogo src={userPartnerIcon} /> : null
  }
  const { isVisible: isEnvironmentStripeVisible } = getEnvironmentStripeInfo()

  return (
    <StyledNavBar marginBottom={isEnvironmentStripeVisible ? '4px' : 0}>
      <NavBarContent data-test='navigation-bar'>
        <NavBarSection left>
          <StyledLogo to='/' className='icon-relay' tabIndex='0' />
          {!canImpersonate && <PartnerLogo />}
          {canImpersonate && <NavPartners />}
          <div id='main-header-left-placeholder' />
        </NavBarSection>
        <NavBarSection central>
          <NavTickets />
          {canReadAdminPages && (
            <>
              <NavConfiguration />
              <NavAppRequests />
              <NavReports />
            </>
          )}

          {(!canReadAdminPages && canReadPowerUserPages) && (
            <>
              <NavAppRequests />
            </>
          )}

          {Boolean(process.env.SAT_API_ENABLED === 'true') && userIsPartnerManager && !isAnyAdmin && IS_PARTNERS_HAVE_ACCESS_TO_SAT_CLIENTS && (
            <NavSatClients />
          )}

          {ticketingSystemsNotEmpty && <NavAbout canAccessApiDoc={canAccessApiDoc} />}
        </NavBarSection>
        <NavBarSection right>
          {ticketingSystemsNotEmpty && IS_RELAY_3268_QUICK_SEARCH && <GlobalSearchNew />}
          {ticketingSystemsNotEmpty && !IS_RELAY_3268_QUICK_SEARCH && <GlobalSearch />}
          {ticketingSystemsNotEmpty && <NavFeedback />}
          <NavUser />
        </NavBarSection>
      </NavBarContent>
      <EnvironmentStripe />
    </StyledNavBar>
  )
}

export default NavBar
